import type {
  AuthenticatedAdmin,
  TransactionExplorerDetail,
  TransactionExplorerEntry,
} from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { ActivityTimeline } from "../components/ActivityTimeline.js";
import {
  ActiveFilters,
  CopyButton,
  CsvExportButton,
  DatePresets,
  detailUrl,
  LoadingState,
  ResultCount,
  RetryBanner,
  readDetailId,
  readUrlFilters,
  toIsoOrUndefined,
  writeDetailId,
  writeUrlFilters,
} from "../components/ExplorerTools.js";
import { asAmount, asMoment } from "../format.js";
import { rememberRecent } from "../operator-memory.js";
import { WALLET_KIND_LABELS } from "../wallet-kinds.js";

interface Filters {
  currency?: string;
  from?: string;
  kind?: string;
  maxAmount?: string;
  minAmount?: string;
  player?: string;
  reference?: string;
  to?: string;
  transactionId?: string;
}

const emptyForm = {
  currency: "",
  from: "",
  kind: "",
  maxAmount: "",
  minAmount: "",
  player: "",
  reference: "",
  to: "",
  transactionId: "",
};

function minorUnits(value: string): string | undefined {
  if (!value) return undefined;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  return match
    ? `${match[1]}${(match[2] ?? "").padEnd(2, "0")}`.replace(/^0+(?=\d)/, "")
    : undefined;
}

const isoStart = (date: string) => toIsoOrUndefined(`${date}T00:00:00.000Z`);
const isoEnd = (date: string) => toIsoOrUndefined(`${date}T23:59:59.999Z`);
const direction = (amount: string) =>
  BigInt(amount) < 0n ? "Debited from player" : "Credited to player";

function ledgerBalanced(detail: TransactionExplorerDetail) {
  const totals = new Map<string, bigint>();
  for (const entry of detail.entries) {
    totals.set(
      entry.amount.currency,
      (totals.get(entry.amount.currency) ?? 0n) + BigInt(entry.amount.amount),
    );
  }
  return [...totals.values()].every((amount) => amount === 0n);
}

function ownerLabel(ownerType: string, ownerId: string | null) {
  if (ownerType === "user") return "Player";
  return ownerId ? `${ownerType.toUpperCase()} · ${ownerId}` : ownerType.toUpperCase();
}
const asFilters = (form: typeof emptyForm): Filters => {
  const maxAmount = minorUnits(form.maxAmount);
  const minAmount = minorUnits(form.minAmount);
  const from = isoStart(form.from);
  const to = isoEnd(form.to);
  return {
    ...(form.currency.trim() ? { currency: form.currency.trim().toUpperCase() } : {}),
    ...(from ? { from } : {}),
    ...(form.kind ? { kind: form.kind } : {}),
    ...(maxAmount ? { maxAmount } : {}),
    ...(minAmount ? { minAmount } : {}),
    ...(form.player.trim() ? { player: form.player.trim() } : {}),
    ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
    ...(to ? { to } : {}),
    ...(form.transactionId.trim() ? { transactionId: form.transactionId.trim() } : {}),
  };
};

export function Transactions({ admin }: { admin: AuthenticatedAdmin }) {
  const [entries, setEntries] = useState<TransactionExplorerEntry[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const initialForm = useRef(readUrlFilters(emptyForm)).current;
  const initialDetail = useRef(readDetailId()).current;
  const [form, setForm] = useState(initialForm);
  const [selected, setSelected] = useState<TransactionExplorerDetail | null>(null);
  const [justification, setJustification] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const detailGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(async (active: Filters, before?: string) => {
    const requestGeneration = ++loadGeneration.current;
    if (!before) {
      detailGeneration.current += 1;
      setSelected(null);
    }
    setPending(true);
    setError(null);
    try {
      const page = await api.transactions({ ...active, ...(before ? { before } : {}) });
      if (loadGeneration.current !== requestGeneration) return;
      setEntries((current) => (before ? [...(current ?? []), ...page.entries] : page.entries));
      setNextCursor(page.nextCursor);
    } catch (caught) {
      if (loadGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load transactions.");
    } finally {
      if (loadGeneration.current === requestGeneration) setPending(false);
    }
  }, []);

  const open = useCallback(
    async (transactionId: string, updateUrl = true) => {
      const requestGeneration = ++detailGeneration.current;
      setPending(true);
      setError(null);
      setJustification("");
      setNotice(null);
      try {
        const found = await api.transaction(transactionId);
        if (detailGeneration.current !== requestGeneration) return;
        setSelected(found);
        if (updateUrl) writeDetailId(transactionId);
        rememberRecent(admin.id, {
          id: `transaction:${transactionId}`,
          label: `Transaction ${transactionId.slice(0, 8)}`,
          search: `?screen=transactions&detail=${encodeURIComponent(transactionId)}`,
        });
      } catch (caught) {
        if (detailGeneration.current !== requestGeneration) return;
        setError(
          caught instanceof ApiError ? caught.message : "Could not load transaction detail.",
        );
      } finally {
        if (detailGeneration.current === requestGeneration) setPending(false);
      }
    },
    [admin.id],
  );

  function closeDetail() {
    detailGeneration.current += 1;
    setPending(false);
    setSelected(null);
    setJustification("");
    setNotice(null);
    writeDetailId(null);
  }

  async function requestReversal() {
    if (!selected) return;
    const requestGeneration = detailGeneration.current;
    const transactionId = selected.transactionId;
    setPending(true);
    setError(null);
    try {
      await api.raiseAction({
        actionType: "wallet.reversal",
        justification,
        payload: { originalTransactionId: transactionId },
        targetId: transactionId,
      });
      if (detailGeneration.current !== requestGeneration) return;
      setNotice("Reversal request sent to the approval queue.");
      setJustification("");
      const refreshed = await api.transaction(transactionId);
      if (detailGeneration.current === requestGeneration) setSelected(refreshed);
    } catch (caught) {
      if (detailGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not request the reversal.");
    } finally {
      if (detailGeneration.current === requestGeneration) setPending(false);
    }
  }

  useEffect(() => {
    const initial = asFilters(initialForm);
    setFilters(initial);
    void load(initial);
    if (initialDetail) void open(initialDetail, false);
  }, [initialDetail, initialForm, load, open]);

  useEffect(() => {
    if (!selected) return;
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [selected]);

  function apply(event: FormEvent) {
    event.preventDefault();
    const next = asFilters(form);
    writeUrlFilters(form);
    setFilters(next);
    writeDetailId(null);
    void load(next);
  }

  function clear() {
    setForm(emptyForm);
    writeUrlFilters(emptyForm);
    setFilters({});
    writeDetailId(null);
    void load({});
  }

  function removeFilter(key: string) {
    const next = { ...filters };
    delete next[key as keyof Filters];
    setFilters(next);
    if (key in form) setForm({ ...form, [key]: "" });
    writeUrlFilters({ ...form, [key]: "" });
    writeDetailId(null);
    void load(next);
  }

  const activeFilters = Object.entries(filters).map(([key, value]) => ({
    key,
    label: key === "transactionId" ? "Transaction" : key.replace(/Amount$/, " amount"),
    value: key === "from" || key === "to" ? value.slice(0, 10) : value,
  }));

  return (
    <section className="body">
      <form className="transaction-filters" onSubmit={apply}>
        <label className="field">
          <span>Currency</span>
          <input
            className="input"
            maxLength={3}
            onChange={(event) => setForm({ ...form, currency: event.target.value })}
            placeholder="e.g. EUR"
            value={form.currency}
          />
        </label>
        <label className="field">
          <span>Minimum amount</span>
          <input
            className="input"
            inputMode="decimal"
            pattern="\d+(\.\d{1,2})?"
            onChange={(event) => setForm({ ...form, minAmount: event.target.value })}
            placeholder="0.00"
            value={form.minAmount}
          />
        </label>
        <label className="field">
          <span>Maximum amount</span>
          <input
            className="input"
            inputMode="decimal"
            pattern="\d+(\.\d{1,2})?"
            onChange={(event) => setForm({ ...form, maxAmount: event.target.value })}
            placeholder="0.00"
            value={form.maxAmount}
          />
        </label>
        <label className="field">
          <span>Player</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, player: event.target.value })}
            placeholder="ID, email or display name"
            value={form.player}
          />
        </label>
        <label className="field">
          <span>Transaction ID</span>
          <input
            className="input mono-12"
            onChange={(event) => setForm({ ...form, transactionId: event.target.value })}
            placeholder="Exact UUID"
            value={form.transactionId}
          />
        </label>
        <label className="field">
          <span>Reference</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, reference: event.target.value })}
            placeholder="Round or payment reference"
            value={form.reference}
          />
        </label>
        <label className="field">
          <span>Movement</span>
          <select
            className="input"
            onChange={(event) => setForm({ ...form, kind: event.target.value })}
            value={form.kind}
          >
            <option value="">All movements</option>
            {Object.entries(WALLET_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>From</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, from: event.target.value })}
            type="date"
            value={form.from}
          />
        </label>
        <label className="field">
          <span>To</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, to: event.target.value })}
            type="date"
            value={form.to}
          />
        </label>
        <DatePresets onSelect={(range) => setForm({ ...form, ...range })} />
        <div className="transaction-filter-actions">
          <button className="btn" disabled={pending} type="submit">
            Apply filters
          </button>
          <button className="btn btn-quiet" disabled={pending} onClick={clear} type="button">
            Clear
          </button>
        </div>
      </form>

      <ActiveFilters filters={activeFilters} onClear={clear} onRemove={removeFilter} />

      {error ? <RetryBanner message={error} onRetry={() => void load(filters)} /> : null}

      {entries ? (
        <div className="result-tools">
          <ResultCount count={entries.length} noun="movement" />
          <CsvExportButton
            filename="transactions.csv"
            headers={[
              "Created at",
              "Player",
              "Player email",
              "Movement",
              "Account",
              "Amount",
              "Currency",
              "Transaction ID",
              "Reference type",
              "Reference",
            ]}
            rows={entries.map((entry) => [
              entry.createdAt,
              entry.player.displayName,
              entry.player.email,
              WALLET_KIND_LABELS[entry.kind],
              entry.accountType,
              entry.amount.amount,
              entry.amount.currency,
              entry.transactionId,
              entry.referenceType,
              entry.referenceId,
            ])}
          />
        </div>
      ) : null}
      <div className="transaction-layout">
        <div className="card table-scroll">
          {!entries ? (
            <LoadingState label="Loading money movements…" />
          ) : entries.length === 0 ? (
            <div className="empty empty-action">
              <p>No money movement matches these filters.</p>
              <button className="btn btn-quiet" onClick={clear} type="button">
                Clear filters
              </button>
            </div>
          ) : (
            <table className="mobile-cards transactions-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Player</th>
                  <th scope="col">Movement</th>
                  <th scope="col">Account</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Amount
                  </th>
                  <th scope="col">Reference</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    aria-label={`Open ${WALLET_KIND_LABELS[entry.kind]} transaction for ${entry.player.displayName ?? entry.player.email}`}
                    className={`clickable-row${selected?.id === entry.id ? " selected" : ""}`}
                    key={entry.id}
                    onClick={() => void open(entry.transactionId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void open(entry.transactionId);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className="label-13 dim" data-label="When">
                      {asMoment(entry.createdAt)}
                    </td>
                    <td data-label="Player">
                      <span className="cell-main">
                        <span className="label-13">
                          {entry.player.displayName ?? entry.player.email}
                        </span>
                        <span className="label-13 faint">{entry.player.email}</span>
                      </span>
                    </td>
                    <td className="label-13" data-label="Movement">
                      {WALLET_KIND_LABELS[entry.kind]}
                    </td>
                    <td className="label-13 dim" data-label="Account">
                      {entry.accountType}
                    </td>
                    <td className="mono-12" data-label="Amount" style={{ textAlign: "right" }}>
                      {asAmount(entry.amount, { sign: true })}
                    </td>
                    <td className="label-13 dim truncate" data-label="Reference">
                      {entry.referenceId ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {nextCursor ? (
            <button
              className="btn btn-quiet load-more-button"
              disabled={pending}
              onClick={() => void load(filters, nextCursor)}
              type="button"
            >
              {pending ? "Loading…" : "Load more"}
            </button>
          ) : null}
        </div>

        {selected ? (
          <dialog
            aria-labelledby="transaction-detail-title"
            className="round-dialog transaction-detail"
            onCancel={closeDetail}
            onClick={(event) => {
              if (event.target === event.currentTarget) closeDetail();
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") closeDetail();
            }}
            ref={dialog}
          >
            <div className="round-dialog-head">
              <div>
                <h2 className="heading-20" id="transaction-detail-title">
                  Movement detail
                </h2>
                <p className="mono-12 faint">{selected.transactionId}</p>
              </div>
              <div className="round-dialog-actions">
                <button
                  aria-label="Close transaction detail"
                  className="btn btn-quiet btn-sm"
                  onClick={closeDetail}
                  type="button"
                >
                  Close
                </button>
                <CopyButton
                  label="Copy link"
                  value={detailUrl("transactions", selected.transactionId)}
                />
              </div>
            </div>
            <div className="round-dialog-body">
              {notice ? (
                <p className="banner banner-quiet" role="status">
                  {notice}
                </p>
              ) : null}
              {error ? (
                <p className="banner" role="alert">
                  {error}
                </p>
              ) : null}
              <section className="transaction-summary" aria-label="Movement summary">
                <div>
                  <span className="dim label-13">{WALLET_KIND_LABELS[selected.kind]}</span>
                  <strong className="transaction-summary-amount">
                    {asAmount(selected.amount, { sign: true })}
                  </strong>
                  <span className="label-13">{direction(selected.amount.amount)}</span>
                </div>
                <dl>
                  <div>
                    <dt>Player</dt>
                    <dd>{selected.player.displayName ?? selected.player.email}</dd>
                  </div>
                  <div>
                    <dt>Account</dt>
                    <dd>{selected.accountType}</dd>
                  </div>
                  <div>
                    <dt>Recorded</dt>
                    <dd>{asMoment(selected.createdAt)}</dd>
                  </div>
                </dl>
              </section>
              <dl className="transaction-identifiers">
                <dt>Transaction</dt>
                <dd className="id-with-copy">
                  <span className="mono-12">{selected.transactionId}</span>
                  <CopyButton value={selected.transactionId} />
                </dd>
                <dt>Ledger entry</dt>
                <dd className="mono-12">{selected.id}</dd>
                <dt>Idempotency key</dt>
                <dd className="mono-12">{selected.idempotencyKey}</dd>
                <dt>Game round</dt>
                <dd className="id-with-copy">
                  {selected.gameRoundId ? (
                    <>
                      <a
                        className="text-link mono-12"
                        href={detailUrl("rounds", selected.gameRoundId)}
                      >
                        {selected.gameRoundId}
                      </a>
                      <CopyButton value={selected.gameRoundId} />
                    </>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt>Player</dt>
                <dd>{selected.player.email}</dd>
                <dt>Player ID</dt>
                <dd className="id-with-copy">
                  <span className="mono-12">{selected.player.id}</span>
                  <CopyButton value={selected.player.id} />
                </dd>
                <dt>Reference type</dt>
                <dd>{selected.referenceType ?? "—"}</dd>
                <dt>Reference</dt>
                <dd className="mono-12">{selected.referenceId ?? "—"}</dd>
                <dt>Reversal of</dt>
                <dd className="id-with-copy">
                  {selected.reversalOf ? (
                    <>
                      <a
                        className="text-link mono-12"
                        href={detailUrl("transactions", selected.reversalOf)}
                      >
                        {selected.reversalOf}
                      </a>
                      <CopyButton value={selected.reversalOf} />
                    </>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt>Approval</dt>
                <dd>
                  {selected.action ? `${selected.action.status} · ${selected.action.id}` : "—"}
                </dd>
              </dl>
              <div className="transaction-detail-card-bar">
                <div>
                  <strong>Money flow</strong>
                  <span className="dim label-13">Every transaction must net to zero.</span>
                </div>
                <span
                  className={`badge ${ledgerBalanced(selected) ? "badge-active" : "badge-banned"}`}
                >
                  <span className="dot" />
                  {ledgerBalanced(selected) ? "Balanced" : "Out of balance"}
                </span>
              </div>
              <div className="table-scroll">
                <table className="mobile-cards">
                  <thead>
                    <tr>
                      <th scope="col">Owner</th>
                      <th scope="col">Account</th>
                      <th scope="col" style={{ textAlign: "right" }}>
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td data-label="Owner">
                          <span className="cell-main">
                            <strong className="label-13">
                              {ownerLabel(entry.ownerType, entry.ownerId)}
                            </strong>
                            {entry.ownerType === "user" && entry.ownerId ? (
                              <span className="mono-12 faint">{entry.ownerId}</span>
                            ) : null}
                          </span>
                        </td>
                        <td data-label="Account">{entry.accountType}</td>
                        <td className="mono-12" data-label="Amount" style={{ textAlign: "right" }}>
                          {asAmount(entry.amount, { sign: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {can(admin.role, "audit.read") ? (
                <ActivityTimeline targetId={selected.transactionId} />
              ) : null}
              {can(admin.role, "wallet.reversal.initiate") &&
              selected.kind !== "reversal" &&
              !selected.action ? (
                <div className="transaction-reversal">
                  <label className="field">
                    <span>Reversal justification</span>
                    <textarea
                      className="input"
                      minLength={20}
                      onChange={(event) => setJustification(event.target.value)}
                      value={justification}
                    />
                  </label>
                  <button
                    className="btn"
                    disabled={pending || justification.trim().length < 20}
                    onClick={() => void requestReversal()}
                    type="button"
                  >
                    Request reversal
                  </button>
                </div>
              ) : null}
            </div>
          </dialog>
        ) : null}
      </div>
    </section>
  );
}
